import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import {
  CopyIprIdToQaRequest,
  FetchElasticSearchLogsRequest,
  GatewaySendCommandRequest,
  GDPRinfo,
  MowerGatewayDebuggingRequest,
  TwinExistsResponse,
} from 'models/apiModels';
import { RTKQueryError } from 'models/errors/errorModels';
import { ToolsError } from 'models/errors/ToolsError';
import {
  RemoteDebugSession,
  RemoteDebugSessionRequest,
} from 'models/remoteDebugSessionModel';
import { logOut } from 'store/authState/authState';
import { selectCurrentEnv } from 'store/envState/envState';
import { displayError } from 'store/errorState/errorState';
import { RootState } from 'store/Store';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_DEV,
  credentials: 'include',
});

const envUrls: Record<string, string> = {
  dev: '',
  qa: process.env.REACT_APP_API_QA ?? '',
  live: process.env.REACT_APP_API_LIVE ?? '',
  mock: 'http://localhost:3000',
};

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  currentStore,
  extraOptions,
) => {
  let currentEnvironment =
    process.env.NODE_ENV === 'development'
      ? 'mock'
      : selectCurrentEnv(currentStore.getState() as RootState);

  // For fetchElasticSearch: Uses the same backend environment as used in frontendurl. The user selects which environment and which node to search in ElasticSearch.
  let isElasticSearchLogsRequest = false;
  if (typeof args !== 'string' && args.url?.includes('/tools/v1/mowers/fetchElasticSearchLogs')) {
    isElasticSearchLogsRequest = true;
  }

  if (isElasticSearchLogsRequest) {
    const currentHostname = window.location.pathname;
    if (currentHostname.includes('dev')) currentEnvironment = 'dev';
    else if (currentHostname.includes('amc-1')) currentEnvironment = 'live';
    else if (currentHostname.includes('localhost')) currentEnvironment = 'mock';
  }

  const adjustedArgs =
    typeof args === 'string'
      ? envUrls[currentEnvironment] + args
      : { ...args, url: envUrls[currentEnvironment] + args.url };

  let result = await rawBaseQuery(adjustedArgs, currentStore, extraOptions);
  if (result.error && result.error.status === 401) {
    const refreshResult = await fetch(`${process.env.REACT_APP_LOGIN_API_URL}/v1/refresh`, {
      credentials: 'include',
    });
    if (refreshResult.status === 200) {
      result = await rawBaseQuery(adjustedArgs, currentStore, extraOptions);
    } else {
      currentStore.dispatch(
        displayError(
          new ToolsError(
            'Authentication failure',
            'Re-authentication failed. Please contact amc-support.',
          ),
        ),
      );
      currentStore.dispatch(logOut());
    }
  }
  return result;
};

export const toolsApi = createApi({
  reducerPath: 'toolsApi',
  // Since we know the shape of all errors that can be returned from the Tools API, we cast the BaseQueryFn to include our own custom error type,
  // which builds upon the standard FetchBaseQueryError type.
  baseQuery: dynamicBaseQuery as BaseQueryFn<string | FetchArgs, unknown, RTKQueryError>,

  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getMowerStatus: builder.query({
      query: (mowerId: string) => ({
        url: `/tools/v1/mowers/${mowerId}/status`,
      }),
    }),

    fetchTwin: builder.query({
      query: (mowerId: string) => ({
        url: `/tools/v1/mowers/${mowerId}/twin`,
        method: 'GET',
      }),
    }),

    getTwinExists: builder.query<TwinExistsResponse, string>({
      query: (mowerId) => ({
        url: `/tools/v1/mowers/${mowerId}/twin/exists`,
        method: `GET`,
      }),
    }),

    deleteTwin: builder.mutation({
      query: (mowerId: string) => ({
        url: `/tools/v1/mowers/${mowerId}/twin`,
        method: 'DELETE',
      }),
    }),

    getGdprReport: builder.mutation<GDPRinfo, { email: string }>({
      query: (body) => ({
        url: '/tools/v1/gdpr',
        method: 'POST',
        body,
      }),
    }),
    getMapFile: builder.query({
      query: (optionsObject: { mowerId: string; mapType: string }) => ({
        url: `/tools/v1/mowers/${optionsObject.mowerId}/map?mapType=${optionsObject.mapType}`,
        responseHandler: (res) => (res.status === 200 ? res.text() : res.json()),
      }),
    }),
    backendPairing: builder.mutation({
      query: (pairingObject) => ({
        url: '/tools/v1/mowers/pairing',
        method: 'POST',
        body: pairingObject,
      }),
    }),

    getAllRemoteDebugSessions: builder.query<RemoteDebugSession[], undefined>({
      query: () => ({ url: '/tools/v1/mowers/remoteDebugSession', method: 'GET' }),
    }),
    postRemoteDebugSession: builder.mutation<undefined, RemoteDebugSessionRequest>({
      query: (remoteDebugSession: RemoteDebugSessionRequest) => {
        const serialNumber = Number(remoteDebugSession.serialNumber.split('-')[0]);
        return {
          url: `/tools/v1/mowers/${serialNumber}-999999999/remoteDebugSession`,
          method: 'POST',
          body: {
            requestedBy: remoteDebugSession.requestedBy,
            endingTimestamp: remoteDebugSession.endingTimestamp,
            mowerEnvironmentSession: remoteDebugSession.mowerEnvironmentSession,
          },
          responseHandler: (res) => (res.status === 200 ? res.text() : res.json()),
        };
      },
    }),
    postMowerGatewayDebugRequest: builder.mutation<undefined, MowerGatewayDebuggingRequest>({
      query: (request) => {
        return {
          url: `/tools/v1/mowers/mowerGatewayDebugging`,
          method: 'POST',
          body: { deviceId: request.deviceId },
          responseHandler: (res) => (res.status === 200 ? res.text() : res.json()),
        };
      },
    }),
    postGatewaySendCommandRequest: builder.mutation<undefined, GatewaySendCommandRequest>({
      query: (request) => {
        return {
          url: `/tools/v1/mowers/gatewaySendCommand`,
          method: 'POST',
          body: {
            deviceId: request.deviceId,
            command: request.command,
          },
        };
      },
    }),

    postCopyIprIdToQaRequest: builder.mutation<undefined, CopyIprIdToQaRequest>({
      query: (request) => {
        return {
          url: `/tools/v1/mowers/copyIprIdToQa`,
          method: 'POST',
          body: {
            iprId: request.iprId,
          },
        };
      },
    }),

    postFetchElasticSearchLogsRequest: builder.mutation<any, FetchElasticSearchLogsRequest>({
      query: (request) => {
        return {
          url: `/tools/v1/mowers/fetchElasticSearchLogs`,
          method: 'POST',
          body: {
            deviceId: request.deviceId,
            startTime: request.startTime,
            endTime: request.endTime,
            environment: request.environment,
          },
          responseHandler: (res: { status: number; text: () => any; json: () => any }) =>
            res.status === 200 ? res.text() : res.json(),
        };
      },
    }),
  }),
});

export const {
  usePostMowerGatewayDebugRequestMutation,
  useBackendPairingMutation,
  useGetGdprReportMutation,
  useGetMapFileQuery,
  useLazyGetTwinExistsQuery,
  useGetMowerStatusQuery,
  useLazyFetchTwinQuery,
  useDeleteTwinMutation,
  useGetAllRemoteDebugSessionsQuery,
  usePostRemoteDebugSessionMutation,
  usePostGatewaySendCommandRequestMutation,
  usePostCopyIprIdToQaRequestMutation,
  usePostFetchElasticSearchLogsRequestMutation,
} = toolsApi;
